import { useState } from 'react';
import { PenTool, Upload, Download, Bell } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Section, GridContainer, DividerDashed } from '../layout';
import { AnimatedSection, ResponsiveWidgetWrapper } from '../shared';
import { InvestorHubMobileWidget } from '../widgets/InvestorHubMobileWidget';

const features = [
  {
    icon: PenTool,
    title: 'Firma de contratos',
    mobileTitle: 'Firma de contratos',
    description:
      'Firma digital integrada. El inversor recibe el contrato y firma desde cualquier dispositivo.',
  },
  {
    icon: Upload,
    title: 'Subida de comprobantes',
    mobileTitle: 'Subida de docs',
    description:
      'El inversor sube directamente sus comprobantes de transferencia. Sin emails de por medio.',
  },
  {
    icon: Download,
    title: 'Descarga de documentos',
    mobileTitle: 'Descarga de docs',
    description:
      'Acceso permanente a todos sus documentos: contratos firmados, certificados, informes.',
  },
  {
    icon: Bell,
    title: 'Notificaciones',
    mobileTitle: 'Notificaciones',
    description: 'Alertas automáticas para que el inversor siempre sepa en qué punto está.',
  },
];

// Border style matching DividerDashed (light intensity)
const innerBorderStyle = { borderColor: 'rgba(228, 228, 231, 0.5)' };

interface FeatureCellProps {
  index: number;
  icon: LucideIcon;
  mobileTitle: string;
  description: string;
  isActive: boolean;
  onSelect: () => void;
}

function FeatureCell({
  index,
  icon: Icon,
  mobileTitle,
  description,
  isActive,
  onSelect,
}: FeatureCellProps) {
  // Only show inner borders: right border for left column (0,2), bottom border for top row (0,1)
  const isLeftColumn = index % 2 === 0;
  const isTopRow = index < 2;
  const borderClasses = [isLeftColumn && 'border-r', isTopRow && 'border-b']
    .filter(Boolean)
    .join(' ');

  if (isActive) {
    return (
      <motion.div
        layout
        className={`border-dashed ${borderClasses} p-4`}
        style={innerBorderStyle}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
      >
        <div className="h-5 flex items-center">
          <Icon size={16} className="text-text-tertiary" />
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <p className="text-sm font-medium text-text-tertiary mt-1 whitespace-nowrap">
            {mobileTitle}
          </p>
          <p className="text-xs text-text-secondary mt-1">{description}</p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.button
      layout
      onClick={onSelect}
      className={`border-dashed ${borderClasses} min-h-14 flex items-center justify-center cursor-pointer touch-manipulation`}
      style={innerBorderStyle}
      transition={{ duration: 0.25, ease: 'easeInOut' }}
    >
      <Icon size={16} className="text-text-tertiary" />
    </motion.button>
  );
}

/**
 * Investor Portal section - Hub del Inversor features.
 */
export function InvestorPortal() {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <Section id="portal" size="md" className="relative z-10">
      <AnimatedSection>
        <GridContainer>
          {/* Hub Label */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12">
            <DividerDashed />
            <div className="flex py-4 justify-start">
              <span className="text-label text-accent font-medium">[04] Hub del Inversor</span>
            </div>
            <DividerDashed />
          </div>

          {/* Header con dashed lines */}
          <div className="col-span-4 md:col-span-8 xl:col-span-6 mt-4">
            <DividerDashed />
            <h2 className="text-2xl md:text-3xl xl:text-4xl font-semibold text-text-primary tracking-tight md:whitespace-nowrap">
              Tus inversores, autosuficientes.
            </h2>
            <p className="mt-4 text-body text-text-secondary max-w-md">Un entorno dedicado para:</p>
            <DividerDashed />

            {/* Mobile: 2x2 accordion grid (< 640px) */}
            <div className="md:hidden mt-6">
              <DividerDashed />
              <div className="grid grid-cols-2">
                {features.map((feature, index) => (
                  <FeatureCell
                    key={feature.title}
                    index={index}
                    icon={feature.icon}
                    mobileTitle={feature.mobileTitle}
                    description={feature.description}
                    isActive={activeFeature === index}
                    onSelect={() => setActiveFeature(index)}
                  />
                ))}
              </div>
              <DividerDashed />
            </div>

            {/* Tablet+: Static 2-column grid (≥ 640px) */}
            <div className="hidden md:grid md:grid-cols-2 gap-x-6 gap-y-4 mt-6">
              {features.map((feature) => (
                <div key={feature.title} className="flex flex-col">
                  <div className="h-5 flex items-center">
                    <feature.icon size={16} className="text-text-tertiary" />
                  </div>
                  <p className="text-sm font-medium text-text-tertiary mt-1">{feature.title}</p>
                  <p className="text-xs text-text-secondary mt-1 max-w-[200px]">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Investor Hub Mobile Widget */}
          <div className="col-span-4 md:col-span-8 xl:col-span-6 mt-8 xl:mt-4 flex items-start justify-center">
            <ResponsiveWidgetWrapper designWidth={280} designHeight={620}>
              <InvestorHubMobileWidget />
            </ResponsiveWidgetWrapper>
          </div>
        </GridContainer>
      </AnimatedSection>
    </Section>
  );
}
